import { QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // an authorisation or validation failure will not fix itself on a retry
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          if (status && status >= 400 && status < 500) return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});

/** Cache keys, centralised so a WebSocket event can invalidate precisely. */
export const queryKeys = {
  me: ['me'] as const,
  adminUsers: {
    list: (search?: string, page?: number) => ['admin', 'users', search ?? '', page ?? 0] as const,
  },
  reference: {
    units: ['reference', 'units'] as const,
    ingredientCategories: ['reference', 'ingredient-categories'] as const,
    dishCategories: ['reference', 'dish-categories'] as const,
    allergens: ['reference', 'allergens'] as const,
    suppliers: ['reference', 'suppliers'] as const,
    equipment: ['reference', 'equipment'] as const,
    menuTypes: ['reference', 'menu-types'] as const,
    settings: ['reference', 'settings'] as const,
  },
  ingredients: {
    all: ['ingredients'] as const,
    list: (query: unknown) => ['ingredients', 'list', query] as const,
    detail: (id: number) => ['ingredients', 'detail', id] as const,
    prices: (id: number) => ['ingredients', 'prices', id] as const,
    usage: (id: number) => ['ingredients', 'usage', id] as const,
  },
  dishes: {
    all: ['dishes'] as const,
    list: (query: unknown) => ['dishes', 'list', query] as const,
    detail: (id: number) => ['dishes', 'detail', id] as const,
    version: (dishId: number, versionId: number) =>
      ['dishes', 'version', dishId, versionId] as const,
    cost: (versionId: number, params: unknown) => ['dishes', 'cost', versionId, params] as const,
  },
  menus: {
    all: ['menus'] as const,
    list: (query: unknown) => ['menus', 'list', query] as const,
    detail: (id: number) => ['menus', 'detail', id] as const,
    summary: (id: number, guests: number, asOf?: string) =>
      ['menus', 'summary', id, guests, asOf] as const,
  },
  orders: {
    all: ['orders'] as const,
    list: (query: unknown) => ['orders', 'list', query] as const,
    detail: (id: number) => ['orders', 'detail', id] as const,
    quotes: (id: number) => ['orders', 'quotes', id] as const,
  },
  clients: {
    all: ['clients'] as const,
    list: (query: unknown) => ['clients', 'list', query] as const,
  },
  procurement: {
    all: ['procurement'] as const,
    list: (query: unknown) => ['procurement', 'list', query] as const,
    detail: (id: number) => ['procurement', 'detail', id] as const,
  },
};
